import type {
	FieldArrayRenderProps,
	FormRenderProps,
} from "@tql/ui-components-react/kendo/form";
import { FieldArray } from "@tql/ui-components-react/kendo/form";
import type { SortableItemUIProps } from "@tql/ui-components-react/kendo/sortable";
import { Sortable } from "@tql/ui-components-react/kendo/sortable";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Form, FormElement } from "@tql/ui-components-react/tql/form/beta";
import { markerPin_01 } from "@tql/ui-components-react/untitled/icons";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

import type { Stop, StopLocation } from "~/features/ftl/shared/ftl-stop.ts";
import {
	createDefaultStops,
	createNewStop,
	toTimezoneSafeDate,
} from "~/features/ftl/shared/ftl-stop.ts";
import type { Step } from "~/features/quote/components/quoting-accordion/ftl-quoting-accordion.tsx";
import { FtlQuotingAccordion } from "~/features/quote/components/quoting-accordion/ftl-quoting-accordion.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import type { ApiError } from "~/shared/types/api-types.ts";
import { sendTenderFtlShipmentTenderEvent } from "~/shared/utils/google-analytics-helper.ts";

import {
	validateFirstAndLastStopTypes,
	validateStopDatesChronological,
} from "../ftl-quotes/create-quote/create-ftl-quote-validators.ts";
import { useTender } from "../ftl-quotes/hooks/use-tender.tsx";
import { FtlShipmentInformationFields } from "./components/ftl-shipment-information-fields/ftl-shipment-information-fields.tsx";
import { ShipmentSortableItemContainer } from "./components/shipment-sortable-item-container/shipment-sortable-item-container.tsx";
import * as styles from "./create-ftl-shipment.module.css";
import { useCreateFtlShipment } from "./hooks/use-create-ftl-shipment.tsx";

type LoadValue = {
	code: string;
	displayName: string;
};

type TrailerType = {
	code: string;
	displayName: string;
	isRefrigerated: boolean;
};

type TrailerSize = {
	code: string;
	displayName: string;
};

export type FTLShipmentForm = {
	customerPo: string;
	trailerType: TrailerType;
	temperature: string;
	trailerSize: TrailerSize;
	loadValue: LoadValue;
	commodity: string;
	totalWeight: number;
	hazmat: boolean;
	requestedRate: number | null;
	specialLoadInstructions: string;
	stops: Stop[];
};

type CreateFTLShipmentStopCommand = {
	stopType: "PICK" | "DROP";
	postalCode: string;
	country: string;
	state: string;
	city: string;
	orderIndex: number;
	longitude: number;
	latitude: number;
	stopDate: Date;
	appointmentInformation: string;
	pickupNumber: string;
	locationName: string;
	streetAddress: string;
};

export type CreateFTLShipmentCommand = {
	trailerType: string;
	trailerSize?: string;
	temperature: string;
	notifyQuoteCreation: boolean;
	loadValueCode: string;
	commodity: string;
	totalWeight: number;
	isHazmat: boolean;
	specialInstructions?: string;
	customerPo?: string;
	requestedRate?: number | null;
	stops: CreateFTLShipmentStopCommand[];
	accessorials: string[];
};

export function mapFormToCreateShipmentCommand(
	shipmentRequest: FTLShipmentForm,
): CreateFTLShipmentCommand {
	const mappedStops = shipmentRequest.stops.map((stop, index) => ({
		stopType: stop.stopType === "Pickup" ? "PICK" : "DROP",
		postalCode: stop.location?.postalCode,
		country: stop.location?.country,
		state: stop.location?.state,
		city: stop.location?.city,
		orderIndex: index + 1,
		longitude: stop.location?.longitude,
		latitude: stop.location?.latitude,
		stopDate: stop.stopDate ? toTimezoneSafeDate(stop.stopDate) : stop.stopDate,
		appointmentInformation: stop.appointmentInformation,
		pickupNumber: stop.stopNumber,
		locationName: stop.location?.location,
		streetAddress: stop.streetAddress,
		shipperName: stop.shipperName,
	})) as unknown as CreateFTLShipmentCommand["stops"];

	return {
		totalWeight: shipmentRequest.totalWeight,
		notifyQuoteCreation: true,
		isHazmat: shipmentRequest.hazmat ?? false,
		specialInstructions: shipmentRequest.specialLoadInstructions || undefined,
		temperature: shipmentRequest.trailerType.isRefrigerated
			? shipmentRequest.temperature
			: "",
		commodity: shipmentRequest.commodity,
		trailerType: shipmentRequest.trailerType.code,
		trailerSize: shipmentRequest.trailerSize.code,
		loadValueCode: shipmentRequest.loadValue.code,
		customerPo: shipmentRequest.customerPo,
		requestedRate: shipmentRequest.requestedRate ?? null,
		stops: mappedStops,
		accessorials: [],
	};
}

export type StopSortableProps = {
	stops: Stop[];
	onRemove: (index: number) => void;
	onCopy: (index: number, locationOptions?: StopLocation[]) => void;
};

export const sortableItemUIContext = createContext<StopSortableProps>(
	{} as StopSortableProps,
);

// Module-level ref to access form state from StopsContainer
const formRef = { current: null as FormRenderProps | null };

const tenderPendingContext = createContext(false);

const handleTenderKeyDown = (e: React.KeyboardEvent) => {
	if (e.key === " " || e.key === "Enter") {
		e.preventDefault();
		if (e.repeat) return;
		formRef.current?.onSubmit(e);
	}
};

export function CreateFtlShipment() {
	const MOBILE_BREAKPOINT = 992;

	const isMobile = useIsMobile(MOBILE_BREAKPOINT);
	const { isInternal } = useDeploymentType();
	const { clearAllNotifications, showError } = useNotifications();

	const [tenderDialog, setTenderDialog] = useState<
		{ type: "success" } | { type: "error"; message: string } | null
	>(null);
	// Mutation pending state only reaches the DOM after a re-render, so a second
	// submit dispatched before that commit would otherwise slip through. Stays set
	// across both legs of the chained create -> tender flow.
	const inFlightRef = useRef(false);
	const { tender, isPending: isTenderPending } = useTender({
		onSuccess: () => {
			inFlightRef.current = false;
			setTenderDialog({ type: "success" });
		},
		onError: (error: ApiError) => {
			inFlightRef.current = false;
			setTenderDialog({
				type: "error",
				message:
					error.error ??
					"We could not tender this quote right now. Please try again.",
			});
		},
	});

	const { isPending: isShipmentCreationPending, createFtlShipment } =
		useCreateFtlShipment({
			onSuccess: (data) => {
				sendTenderFtlShipmentTenderEvent(data.id.toString());
				tender(data.id);
			},
			// On success the ref is released by useTender instead, so the guard
			// covers the tender leg too.
			onError: () => {
				inFlightRef.current = false;
			},
		});

	const isTendering = isShipmentCreationPending || isTenderPending;

	useAutoSpinner(isTendering);

	const hostRef = useRef<HTMLDivElement>(null);

	const handleSubmit = useCallback(
		(shipmentRequest: FTLShipmentForm) => {
			if (inFlightRef.current) return;

			const stopTypeError = validateFirstAndLastStopTypes(
				shipmentRequest.stops,
			);

			if (stopTypeError) {
				clearAllNotifications(); // To show only one error at a time.
				showError(stopTypeError, { closable: true });
				return; // Prevent submission
			}

			// Validate chronological order of stop dates
			const chronologicalError = validateStopDatesChronological(
				shipmentRequest.stops,
			);

			if (chronologicalError) {
				clearAllNotifications(); // To show only one error at a time.
				showError(chronologicalError, { closable: true });
				return; // Prevent submission
			}

			inFlightRef.current = true;
			createFtlShipment(mapFormToCreateShipmentCommand(shipmentRequest));
		},
		[clearAllNotifications, createFtlShipment, showError],
	);
	return (
		<div
			ref={hostRef}
			className={styles.ftlShipmentContainer}
			data-testid={"create-ftl-shipment"}
		>
			{isMobile ? null : (
				<TraxPageHeader
					representativeType={"ftl"}
					showSupportMessage={!isInternal}
					title={"Tender FTL Shipment"}
				/>
			)}
			<Form
				initialValues={
					{
						hazmat: false,
						temperature: "",
						specialLoadInstructions: "",
						stops: createDefaultStops(),
					} as FTLShipmentForm
				}
				onSubmit={(values: FTLShipmentForm) => handleSubmit(values)}
				render={(formRenderProps: FormRenderProps) => {
					// Store formRenderProps in ref for access in StopsContainer
					formRef.current = formRenderProps;

					const selectedTrailerType = formRenderProps.valueGetter(
						"trailerType",
					) as TrailerType | undefined;

					const steps: Step[] = [
						{
							step: 1,
							title: "Shipment Information",
							id: "shipment-information",
							active: true,
							completed: false,
							content: (
								<div className={"k-pl-5 k-pt-5 k-pr-5"}>
									<FormElement>
										<FtlShipmentInformationFields
											selectedTrailerTypeCode={selectedTrailerType?.code}
											selectedTrailerTypeIsRefrigerated={
												selectedTrailerType?.isRefrigerated === true
											}
										/>
										<tenderPendingContext.Provider value={isTendering}>
											<FieldArray component={StopsContainer} name={"stops"} />
										</tenderPendingContext.Provider>
									</FormElement>
								</div>
							),
						},
					];

					return <FtlQuotingAccordion steps={steps} />;
				}}
			/>

			{tenderDialog?.type === "success" && (
				<TraxModal
					showConfirmationButtons
					appendTo={hostRef.current}
					confirmText={"Tender Another Shipment"}
					onClose={() => setTenderDialog(null)}
					onConfirm={() => {
						setTenderDialog(null);
						formRef.current?.onFormReset?.();
					}}
					showCancelButton={false}
					title={"Shipment Tendered"}
					type={"primary"}
				>
					<p>Your shipment was tendered successfully.</p>
				</TraxModal>
			)}

			{tenderDialog?.type === "error" && (
				<TraxModal
					showConfirmationButtons
					appendTo={hostRef.current}
					confirmText={"Edit Shipment"}
					onClose={() => setTenderDialog(null)}
					onConfirm={() => {
						setTenderDialog(null);
					}}
					showCancelButton={false}
					title={"Tender Failed"}
					type={"primary"}
				>
					<p>
						{tenderDialog.message ??
							"We could not tender this quote right now. Please try again."}
					</p>
				</TraxModal>
			)}
		</div>
	);
}

function insertCopiedStop(
	stops: Stop[],
	index: number,
	copiedStop: Stop,
): Stop[] {
	return [...stops.slice(0, index + 1), copiedStop, ...stops.slice(index + 1)];
}

function StopsContainer({ value, ...props }: Readonly<FieldArrayRenderProps>) {
	const stops = value as Stop[];
	const isTendering = useContext(tenderPendingContext);

	const onRemove = useCallback(
		(index: number) => {
			props.onRemove({ index });
		},
		[props],
	);

	const onCopy = useCallback(
		(index: number, locationOptions?: StopLocation[]) => {
			const stopToCopy = stops[index];
			if (stopToCopy) {
				const newStop = {
					...stopToCopy,
					id: crypto.randomUUID(),
					orderIndex: index + 2,
					locationOptions,
				};

				formRef.current?.onChange("stops", {
					value: insertCopiedStop(stops, index, newStop),
				});
			}
		},
		[stops],
	);

	const addStop = () => {
		props.onPush({ value: createNewStop() });
	};

	const contextValue = useMemo(
		() => ({
			stops,
			onRemove,
			onCopy,
		}),
		[stops, onRemove, onCopy],
	);

	const handleAddStopKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === " " || e.key === "Enter") {
			e.preventDefault();
			if (e.repeat) return;
			addStop();
		}
	};

	return (
		<>
			<div className={styles.routeStops}>
				<sortableItemUIContext.Provider value={contextValue}>
					<Sortable
						data={stops}
						idField={"id"}
						itemUI={SortableItemUI}
						onDragOver={props.onMove}
					/>
				</sortableItemUIContext.Provider>
			</div>
			<div className={styles.buttonContainer}>
				<Button
					formAction={"none"}
					onClick={addStop}
					onKeyDown={handleAddStopKeyDown}
					svgIcon={markerPin_01}
					variant={"secondary"}
				>
					Add Stop
				</Button>
				<Button
					formAction={"none"}
					loading={isTendering}
					onClick={(event) => {
						if (isTendering) return;
						formRef.current?.onSubmit(event);
					}}
					onKeyDown={handleTenderKeyDown}
					variant={"primary"}
				>
					Tender Shipment
				</Button>
			</div>
		</>
	);
}

function SortableItemUI(props: Readonly<SortableItemUIProps>) {
	return <ShipmentSortableItemContainer {...props} />;
}
